import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Alert, AlertDescription, Card, Spinner } from '@agion/ui'
import { Server, Activity, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { EnvironmentCard } from '@/features/environment/InfraEnvironmentCard'
import { listEnvironments } from '@/features/environment/infra_environment.service'
import type { CloudEnvironment } from '@/core/infra_types'

export function InfraDashboardPage() {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [environments, setEnvironments] = useState<CloudEnvironment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        setIsLoading(true)
        const result = await listEnvironments({ perPage: 50 })
        setEnvironments(Array.isArray(result.data) ? result.data : [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load environments')
      } finally {
        setIsLoading(false)
      }
    }
    void load()
  }, [])

  const activeCount = environments.filter(e => e.status === 'active').length
  const failedCount = environments.filter(e => e.status === 'failed').length
  const provisioningCount = environments.filter(
    e => e.status === 'provisioning' || e.status === 'updating'
  ).length

  return (
    <div className="container-standard">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Server className="text-blue-600" size={28} />
          <div>
            <h1 className="page-title">{t('pages:dashboard.title', 'Infrastructure Dashboard')}</h1>
            <p className="page-description">
              {t('pages:dashboard.description', 'Overview of cloud environments and operations')}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card data-testid="stat-total">
          <div className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <Server className="h-6 w-6 text-white" />
            </div>
            <div>
              <p data-testid="stat-total-value" className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {environments.length}
              </p>
              <p className="text-xs text-slate-500">
                {t('pages:dashboard.stats.total', 'Total Environments')}
              </p>
            </div>
          </div>
        </Card>
        <Card data-testid="stat-active">
          <div className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <p data-testid="stat-active-value" className="text-2xl font-bold text-green-600">{activeCount}</p>
              <p className="text-xs text-slate-500">
                {t('pages:dashboard.stats.active', 'Active')}
              </p>
            </div>
          </div>
        </Card>
        <Card data-testid="stat-in-progress">
          <div className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div>
              <p data-testid="stat-in-progress-value" className="text-2xl font-bold text-amber-600">{provisioningCount}</p>
              <p className="text-xs text-slate-500">
                {t('pages:dashboard.stats.inProgress', 'In Progress')}
              </p>
            </div>
          </div>
        </Card>
        <Card data-testid="stat-failed">
          <div className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-white" />
            </div>
            <div>
              <p data-testid="stat-failed-value" className="text-2xl font-bold text-red-600">{failedCount}</p>
              <p className="text-xs text-slate-500">
                {t('pages:dashboard.stats.failed', 'Failed')}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Environment Cards Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : environments.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {t('pages:dashboard.empty', 'No environments configured yet.')}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {environments.map(env => (
            <EnvironmentCard
              key={env.id}
              environment={env}
              onClick={() => navigate(`/environments/${env.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
